import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { T, US_STATES } from '../tokens';

// ─── Icons ────────────────────────────────────────────────────────────────────

const ArrowLeft = () => (
  <svg width={22} height={22} viewBox="0 0 24 24" fill="none">
    <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const DownloadIcon = ({ size = 20 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path d="M12 3v13M7 11l5 5 5-5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M3 21h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const CalendarIcon = () => (
  <svg width={16} height={16} viewBox="0 0 24 24" fill="none">
    <rect x="3" y="4" width="18" height="18" rx="3" stroke="currentColor" strokeWidth="1.8" />
    <path d="M3 10h18M8 2v4M16 2v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const CheckIcon = () => (
  <svg width={14} height={14} viewBox="0 0 24 24" fill="none">
    <path d="M5 12l5 5L19 7" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const SpinnerIcon = () => (
  <svg width={18} height={18} viewBox="0 0 24 24" fill="none" style={{ animation: 'spin 0.8s linear infinite' }}>
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" strokeOpacity="0.2" />
    <path d="M21 12a9 9 0 00-9-9" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
  </svg>
);

// ─── Types ────────────────────────────────────────────────────────────────────

interface Survey {
  id: number;
  name: string;
  type: string;
  description: string;
  location: string;
  numRecorders: number;
  recorderNames: string[];
  sections: string[];
  windSpeed: number;
  cloudCoverage: number;
  temperature: number;
  surveyDate: string;
  startTime: number | null;
  endTime: number | null;
  secondsElapsed: number | null;
  submittedAt: string | null;
  comments: string;
}

interface SurveyDetail {
  id: { surveyId: number; speciesId: number };
  butterfly: {
    id: number;
    commonName: string;
    scientificName: string;
    commonFamilyName: string;
    scientificFamilyName: string;
  };
  sectionSightings: string[] | null;
  total: number;
  notes: string | null;
}

type ExportFormat = 'json' | 'csv';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function stateName(code: string) {
  const found = US_STATES.find(([c]) => c === code);
  return found ? found[1] : (code || '');
}

function formatDuration(seconds: number | null | undefined) {
  if (!seconds || seconds <= 0) return '';
  const m = Math.floor(seconds / 60);
  const h = Math.floor(m / 60);
  return h > 0 ? `${h}h ${m % 60}m` : `${m}m`;
}

function isoDate(dateStr: string | null | undefined) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return isNaN(d.getTime()) ? dateStr : d.toISOString().split('T')[0];
}

function isCompleted(s: Survey) {
  return s.secondsElapsed != null && s.secondsElapsed > 0;
}

function inDateRange(s: Survey, from: string, to: string) {
  const d = isoDate(s.surveyDate);
  if (!d) return true;
  if (from && d < from) return false;
  if (to && d > to) return false;
  return true;
}

// Build JSON export object for one survey + its details
function buildJsonSurvey(survey: Survey, details: SurveyDetail[]) {
  return {
    id: survey.id,
    name: survey.name || '',
    type: survey.type || '',
    status: isCompleted(survey) ? 'submitted' : 'draft',
    date: isoDate(survey.surveyDate),
    location: {
      state: survey.location || '',
      stateName: stateName(survey.location),
    },
    recorders: {
      count: survey.numRecorders || 0,
      names: survey.recorderNames || [],
    },
    sections: survey.sections || [],
    conditions: {
      temperature_c: survey.temperature ?? null,
      windSpeed_kph: survey.windSpeed ?? null,
      cloudCoverage_pct: survey.cloudCoverage ?? null,
    },
    duration: formatDuration(survey.secondsElapsed) || null,
    secondsElapsed: survey.secondsElapsed ?? null,
    description: survey.description || '',
    comments: survey.comments || '',
    sightings: details.map(d => ({
      speciesId: d.butterfly?.id ?? d.id?.speciesId,
      commonName: d.butterfly?.commonName ?? '',
      scientificName: d.butterfly?.scientificName ?? '',
      family: d.butterfly?.commonFamilyName ?? d.butterfly?.scientificFamilyName ?? '',
      total: d.total,
      sectionCounts: d.sectionSightings ?? [],
      notes: d.notes ?? '',
    })),
  };
}

// Build flat CSV rows for one survey + its details
function buildCsvRows(survey: Survey, details: SurveyDetail[]): string[][] {
  const base = [
    String(survey.id),
    survey.name || '',
    survey.type || '',
    isCompleted(survey) ? 'submitted' : 'draft',
    isoDate(survey.surveyDate),
    survey.location || '',
    stateName(survey.location),
    String(survey.numRecorders || 0),
    (survey.recorderNames || []).join('; '),
    (survey.sections || []).join('; '),
    survey.temperature != null ? String(survey.temperature) : '',
    survey.windSpeed != null ? String(survey.windSpeed) : '',
    survey.cloudCoverage != null ? String(survey.cloudCoverage) : '',
    formatDuration(survey.secondsElapsed),
    survey.description || '',
    survey.comments || '',
  ];

  if (details.length === 0) {
    return [[...base, '', '', '', '', '', '']];
  }

  return details.map(d => [
    ...base,
    d.butterfly?.commonName ?? '',
    d.butterfly?.scientificName ?? '',
    d.butterfly?.commonFamilyName ?? d.butterfly?.scientificFamilyName ?? '',
    String(d.total),
    (d.sectionSightings ?? []).join('; '),
    d.notes ?? '',
  ]);
}

const CSV_HEADER = [
  'Survey ID', 'Name', 'Type', 'Status', 'Date', 'State Code', 'State',
  'Recorders', 'Recorder Names', 'Sections',
  'Temperature (°C)', 'Wind Speed (kph)', 'Cloud Coverage (%)',
  'Duration', 'Description', 'Comments',
  'Species', 'Scientific Name', 'Family', 'Total Count', 'Section Counts', 'Notes',
];

function escapeCsv(val: string) {
  if (val.includes(',') || val.includes('"') || val.includes('\n')) {
    return '"' + val.replace(/"/g, '""') + '"';
  }
  return val;
}

function buildCsvString(rows: string[][]): string {
  const lines = [CSV_HEADER, ...rows].map(row => row.map(escapeCsv).join(','));
  return lines.join('\r\n');
}

function triggerDownload(content: string, filename: string, mime: string) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Section label ────────────────────────────────────────────────────────────

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      fontFamily: T.font, fontSize: 11, fontWeight: 600, color: T.ink3,
      letterSpacing: '0.08em', textTransform: 'uppercase', margin: '22px 0 8px',
    }}>
      {children}
    </div>
  );
}

// ─── Format pill ──────────────────────────────────────────────────────────────

function FormatPill({
  label, sublabel, selected, onClick,
}: {
  label: string; sublabel: string; selected: boolean; onClick: () => void;
}) {
  return (
    <button onClick={onClick} style={{
      flex: 1, textAlign: 'left', padding: '14px 16px',
      borderRadius: T.r.md, cursor: 'pointer',
      border: selected ? `2px solid ${T.forest}` : `1.5px solid ${T.line}`,
      background: selected ? T.forestSoft : T.card,
      transition: 'border-color 0.15s, background 0.15s',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{
          fontFamily: T.fontDisplay, fontSize: 16, fontWeight: 700,
          color: selected ? T.forest : T.ink, letterSpacing: '0.04em',
        }}>
          .{label}
        </div>
        {selected && (
          <div style={{
            width: 20, height: 20, borderRadius: '50%',
            background: T.forest, color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <CheckIcon />
          </div>
        )}
      </div>
      <div style={{ fontFamily: T.font, fontSize: 12, color: T.ink2, marginTop: 3 }}>
        {sublabel}
      </div>
    </button>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function ExportPage() {
  const navigate = useNavigate();

  const today = new Date().toISOString().split('T')[0];
  const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

  const [fromDate, setFromDate] = useState(thirtyDaysAgo);
  const [toDate, setToDate] = useState(today);
  const [format, setFormat] = useState<ExportFormat>('csv');
  const [includeDetails, setIncludeDetails] = useState(true);

  const [allSurveys, setAllSurveys] = useState<Survey[]>([]);
  const [loadingSurveys, setLoadingSurveys] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [exportDone, setExportDone] = useState(false);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const userId: number | undefined = user?.userId;

  useEffect(() => {
    if (!userId) { setLoadingSurveys(false); return; }
    fetch(`/api/surveys/user/${userId}`)
      .then(r => r.ok ? r.json() : Promise.reject())
      .then((data: Survey[]) => setAllSurveys(data))
      .catch(() => {})
      .finally(() => setLoadingSurveys(false));
  }, [userId]);

  useEffect(() => {
      if(localStorage.getItem("user") == null) {
        localStorage.clear();
        navigate("/")
      }
  }, [])

  const matchingSurveys = allSurveys.filter(s => inDateRange(s, fromDate, toDate));

  const handleExport = useCallback(async () => {
    if (matchingSurveys.length === 0 || exporting) return;
    setExporting(true);
    setExportDone(false);

    try {
      // Fetch details for each matching survey if requested
      const surveysWithDetails: { survey: Survey; details: SurveyDetail[] }[] = await Promise.all(
        matchingSurveys.map(async survey => {
          if (!includeDetails) return { survey, details: [] };
          try {
            const res = await fetch(`/api/surveys/${survey.id}/details`);
            const details: SurveyDetail[] = res.ok ? await res.json() : [];
            return { survey, details };
          } catch {
            return { survey, details: [] };
          }
        })
      );

      const dateTag = `${fromDate}_to_${toDate}`;

      if (format === 'json') {
        const payload = surveysWithDetails.map(({ survey, details }) =>
          buildJsonSurvey(survey, details)
        );
        triggerDownload(
          JSON.stringify(payload, null, 2),
          `surveys_${dateTag}.json`,
          'application/json'
        );
      } else {
        const allRows: string[][] = surveysWithDetails.flatMap(({ survey, details }) =>
          buildCsvRows(survey, details)
        );
        triggerDownload(
          buildCsvString(allRows),
          `surveys_${dateTag}.csv`,
          'text/csv'
        );
      }

      setExportDone(true);
      setTimeout(() => setExportDone(false), 3000);
    } finally {
      setExporting(false);
    }
  }, [matchingSurveys, format, includeDetails, fromDate, toDate, exporting]);

  const dateInput = (label: string, value: string, onChange: (v: string) => void) => (
    <div style={{ flex: 1 }}>
      <div style={{ fontFamily: T.font, fontSize: 13, fontWeight: 500, color: T.ink2, marginBottom: 6 }}>
        {label}
      </div>
      <div style={{ position: 'relative' }}>
        <input
          type="date"
          value={value}
          onChange={e => onChange(e.target.value)}
          max={today}
          style={{
            width: '100%', boxSizing: 'border-box',
            height: 44, padding: '0 12px',
            fontFamily: T.font, fontSize: 15, color: T.ink,
            background: T.card, border: `1px solid ${T.line}`,
            borderRadius: T.r.md, outline: 'none',
            appearance: 'none', WebkitAppearance: 'none',
          }}
        />
      </div>
    </div>
  );

  return (
    <div className="lep-page">
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <div className="lep-page-inner" style={{ padding: '0 0 80px' }}>

        {/* Header */}
        <div style={{
          padding: '54px 20px 20px',
          borderBottom: `1px solid ${T.line}`,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
            <button
              onClick={() => navigate('/menu')}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: T.ink2, padding: 4, margin: -4,
                display: 'flex', alignItems: 'center',
              }}
            >
              <ArrowLeft />
            </button>
            <div style={{
              fontFamily: T.fontDisplay, fontSize: 22, fontWeight: 600,
              color: T.ink, letterSpacing: '-0.02em',
            }}>
              Export Surveys
            </div>
          </div>
          <div style={{ fontFamily: T.font, fontSize: 13, color: T.ink2, paddingLeft: 38 }}>
            Download your survey data as JSON or CSV
          </div>
        </div>

        <div style={{ padding: '0 20px' }}>

          {/* Date range */}
          <SectionLabel>Date range</SectionLabel>
          <div style={{ display: 'flex', gap: 12 }}>
            {dateInput('From', fromDate, setFromDate)}
            {dateInput('To', toDate, setToDate)}
          </div>

          {/* Quick presets */}
          <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
            {[
              ['Last 7 days', 7],
              ['Last 30 days', 30],
              ['Last 90 days', 90],
              ['This year', 'year'],
              ['All time', 'all'],
            ].map(([label, val]) => (
              <button
                key={label as string}
                onClick={() => {
                  const t = new Date().toISOString().split('T')[0];
                  setToDate(t);
                  if (val === 'year') {
                    setFromDate(`${new Date().getFullYear()}-01-01`);
                  } else if (val === 'all') {
                    setFromDate('2000-01-01');
                  } else {
                    const d = new Date(Date.now() - (val as number) * 24 * 60 * 60 * 1000);
                    setFromDate(d.toISOString().split('T')[0]);
                  }
                }}
                style={{
                  fontFamily: T.font, fontSize: 12, fontWeight: 500,
                  color: T.ink2, background: T.bgDeep,
                  border: `1px solid ${T.line}`, borderRadius: T.r.pill,
                  padding: '5px 12px', cursor: 'pointer',
                }}
              >
                {label as string}
              </button>
            ))}
          </div>

          {/* Match count */}
          <div style={{
            marginTop: 14, padding: '12px 14px',
            background: matchingSurveys.length > 0 ? T.forestSoft : T.bgDeep,
            borderRadius: T.r.md,
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ color: matchingSurveys.length > 0 ? T.forest : T.ink3 }}>
                <CalendarIcon />
              </span>
            </div>
            {loadingSurveys ? (
              <div style={{ fontFamily: T.font, fontSize: 14, color: T.ink3 }}>Loading…</div>
            ) : (
              <div style={{ fontFamily: T.font, fontSize: 14, color: matchingSurveys.length > 0 ? T.forest : T.ink2 }}>
                <strong>{matchingSurveys.length}</strong>{' '}
                {matchingSurveys.length === 1 ? 'survey' : 'surveys'} in this range
                {matchingSurveys.length > 0 && (
                  <span style={{ color: T.ink3, marginLeft: 6 }}>
                    ({matchingSurveys.filter(s => s.secondsElapsed != null && s.secondsElapsed > 0).length} submitted,{' '}
                    {matchingSurveys.filter(s => !s.secondsElapsed || s.secondsElapsed <= 0).length} drafts)
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Format */}
          <SectionLabel>Format</SectionLabel>
          <div style={{ display: 'flex', gap: 10 }}>
            <FormatPill
              label="csv"
              sublabel="Spreadsheet — opens in Excel, Sheets"
              selected={format === 'csv'}
              onClick={() => setFormat('csv')}
            />
            <FormatPill
              label="json"
              sublabel="Structured data — nested sightings"
              selected={format === 'json'}
              onClick={() => setFormat('json')}
            />
          </div>

          {/* Options */}
          <SectionLabel>Options</SectionLabel>
          <button
            onClick={() => setIncludeDetails(v => !v)}
            style={{
              width: '100%', textAlign: 'left', background: T.card,
              border: `1px solid ${T.line}`, borderRadius: T.r.md,
              padding: '14px 16px', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}
          >
            <div>
              <div style={{ fontFamily: T.font, fontSize: 15, fontWeight: 500, color: T.ink }}>
                Include butterfly sightings
              </div>
              <div style={{ fontFamily: T.font, fontSize: 12, color: T.ink2, marginTop: 2 }}>
                {includeDetails
                  ? 'Each species will be included in the export'
                  : 'Only survey metadata will be exported'}
              </div>
            </div>
            <div style={{
              width: 44, height: 26, borderRadius: 13,
              background: includeDetails ? T.forest : T.bgDeep,
              border: `1.5px solid ${includeDetails ? T.forest : T.lineStrong}`,
              position: 'relative', transition: 'background 0.2s, border-color 0.2s', flexShrink: 0,
            }}>
              <div style={{
                position: 'absolute', top: 2,
                left: includeDetails ? 18 : 2,
                width: 18, height: 18, borderRadius: '50%',
                background: '#fff',
                boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                transition: 'left 0.2s',
              }} />
            </div>
          </button>

          {/* What's included preview */}
          {format === 'csv' && (
            <div style={{
              marginTop: 10, padding: '10px 12px',
              background: T.bgDeep, borderRadius: T.r.sm,
            }}>
              <div style={{ fontFamily: T.font, fontSize: 11, fontWeight: 600, color: T.ink3, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>
                Columns included
              </div>
              <div style={{ fontFamily: T.fontMono, fontSize: 11, color: T.ink2, lineHeight: 1.8 }}>
                Survey ID · Name · Type · Status · Date · State · Recorders · Sections · Temperature · Wind · Cloud Coverage · Duration · Description · Comments
                {includeDetails && ' · Species · Scientific Name · Family · Total Count · Section Counts · Notes'}
              </div>
            </div>
          )}

          {format === 'json' && (
            <div style={{
              marginTop: 10, padding: '10px 12px',
              background: T.bgDeep, borderRadius: T.r.sm,
              fontFamily: T.fontMono, fontSize: 11, color: T.ink2, lineHeight: 1.7,
            }}>
              {'[{ id, name, type, status, date, location, recorders, sections, conditions, duration'}{includeDetails ? ', sightings: [{ commonName, scientificName, family, total, sectionCounts }]' : ''}{' }]'}
            </div>
          )}
        </div>

        {/* Sticky export button */}
        <div className="lep-sticky-footer">
          <button
            onClick={handleExport}
            disabled={matchingSurveys.length === 0 || exporting || loadingSurveys}
            style={{
              width: '100%', height: 52, borderRadius: T.r.pill,
              background: exportDone ? '#2A7A35' : matchingSurveys.length === 0 ? T.bgDeep : T.forest,
              border: 'none', cursor: matchingSurveys.length === 0 ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
              fontFamily: T.fontDisplay, fontSize: 17, fontWeight: 600, color: '#fff',
              letterSpacing: '-0.01em',
              boxShadow: matchingSurveys.length > 0 ? '0 4px 16px rgba(45,80,22,0.25)' : 'none',
              transition: 'background 0.2s',
            }}
          >
            {exporting ? (
              <>
                <SpinnerIcon />
                Exporting…
              </>
            ) : exportDone ? (
              <>
                <CheckIcon />
                Downloaded
              </>
            ) : (
              <>
                <DownloadIcon size={20} />
                {matchingSurveys.length === 0
                  ? 'No surveys in range'
                  : `Export ${matchingSurveys.length} ${matchingSurveys.length === 1 ? 'survey' : 'surveys'} as .${format}`}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
